<template>
  <div
    ref="dropdownRef"
    :class="[
      'simple-dropdown-ui',
      {
        'simple-dropdown-ui--open': showModal,
        'v-dropdown-ui--settled': !floating,
      },
    ]"
  >
    <div class="simple-dropdown-ui__toggle-btn">
      <slot
        name="default"
        aria-haspopup="listbox"
        :set-toggle-ref="setToggleRef"
        :is-active="showModal"
        :toggle="() => setShowModal(!showModal)"
      />
    </div>
    <div
      v-show="showModal"
      ref="modalRef"
      class="simple-dropdown-ui__modal"
      role="dialog"
      :style="modalFloatingStyles"
    >
      <ul class="simple-dropdown-ui__modal-list" role="listbox">
        <li
          v-for="(option, index) in options"
          :key="index"
          class="simple-dropdown-ui__modal-list-item"
          :data-value="option.title"
        >
          <button
            :ref="setDropdownModalButtonRefs"
            type="button"
            :class="[
              'simple-dropdown-ui__modal-button',
              option.danger && 'simple-dropdown-ui__modal-button--danger',
              option.disabled && 'simple-dropdown-ui__modal-button--disabled',
            ]"
            role="option"
            :disabled="option.disabled"
            @click.capture.stop.prevent="handleSelect(option)"
          >
            <span class="simple-dropdown-ui__modal-button-title">
              {{ option.title }}
            </span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { nextTick, ref } from 'vue';
  import { offset, shift, autoUpdate, useFloating, size } from '@floating-ui/vue';

  // Types
  import type { PropType, ComponentPublicInstance, Ref, StyleValue } from 'vue';
  import type { Placement } from '@floating-ui/dom';
  import type { TSimpleDropdownOption } from '~/misc/simple-dropdown/types';

  const props = defineProps({
    options: {
      type: Array as PropType<TSimpleDropdownOption[]>,
      default: (): TSimpleDropdownOption[] => [],
    },
    floating: {
      type: Boolean,
      default: false,
    },
    floatingModalOffsetInPx: {
      type: Number,
      default: 10,
    },
    placement: {
      type: String as PropType<Placement>,
      default: 'bottom-end',
    },
    autoResize: {
      type: Boolean,
      default: true,
    },
  });

  const dropdownRef: Ref<HTMLDivElement | null> = ref<HTMLDivElement | null>(null);
  const modalRef: Ref<HTMLDivElement | null> = ref<HTMLDivElement | null>(null);
  const toggleRef: Ref<HTMLElement | null> = ref<HTMLButtonElement | null>(null);
  const focusedBtnIndex: Ref<number> = ref<number>(0);
  const dropdownModalButtonRefs: Ref<HTMLButtonElement[]> = ref<HTMLButtonElement[]>([]);
  const showModal: Ref<boolean> = ref<boolean>(false);
  const isLeftPosition: Ref<boolean> = ref<boolean>(true);

  let modalFloatingStyles: StyleValue = '';

  if (props.floating) {
    const { floatingStyles } = useFloating(dropdownRef, modalRef, {
      placement: props.placement ?? (isLeftPosition.value ? 'bottom-start' : 'bottom-end'),
      transform: false,
      middleware: [
        offset(props.floatingModalOffsetInPx),
        shift(),
        size({
          apply({ elements }) {
            if (props.autoResize) {
              Object.assign(elements.floating.style, {
                maxWidth: `${elements.reference.getBoundingClientRect().width}px`,
              });
            }
          },
        }),
      ],
      whileElementsMounted: autoUpdate,
    });

    modalFloatingStyles = floatingStyles;
  }

  const setShowModal = (state: boolean) => {
    showModal.value = state;
  };

  const setDropdownModalPosition = () => {
    const modalEl = modalRef.value as HTMLDivElement;

    if (!modalEl) {
      return;
    }

    const dropdownModalClientRect: DOMRect = modalEl.getBoundingClientRect();

    isLeftPosition.value =
      dropdownModalClientRect.width + dropdownModalClientRect.left < window.innerWidth;
  };

  const focusToggleBtn = () => {
    const toggleEl = toggleRef.value as HTMLButtonElement;

    if (toggleEl) {
      toggleEl.focus();
    }
  };

  const handleSelect = (option: TSimpleDropdownOption) => {
    setShowModal(false);
    option.clickCallback?.();
  };

  const handleDropdownMousedown = (event: MouseEvent) => {
    const target: EventTarget | null = event.target;
    const dropdownEl = dropdownRef.value as HTMLDivElement;

    if (!target || !dropdownEl) {
      return;
    }

    if (!dropdownEl.contains(target as Node) && dropdownEl !== event.target) {
      setShowModal(false);
    }
  };

  const toggleDropdownMousedownEventListener = (state: boolean) => {
    state
      ? document.addEventListener('mousedown', handleDropdownMousedown)
      : document.removeEventListener('mousedown', handleDropdownMousedown);
  };

  const setToggleRef = (el: Element | ComponentPublicInstance | null) => {
    if (!el) {
      toggleRef.value = null;

      return;
    }

    if ('$el' in el && el.$el instanceof HTMLElement) {
      toggleRef.value = el.$el;
    } else if (el instanceof HTMLElement) {
      toggleRef.value = el;
    }
  };

  const setDropdownModalButtonRefs = (ref: Element | ComponentPublicInstance | null) => {
    if (ref) {
      dropdownModalButtonRefs.value.push(ref as HTMLButtonElement);
    }
  };

  const toggleArrowsNavListener = (state: boolean) => {
    if (state) {
      document.addEventListener('keydown', arrowsNavHandler);
      focusItemByIndex(0);
    } else {
      focusToggleBtn();
      document.removeEventListener('keydown', arrowsNavHandler);
    }
  };

  const arrowsNavHandler = (event: KeyboardEvent) => {
    switch (event.keyCode) {
      case 38: {
        event.preventDefault();
        focusPrevItem();
        break;
      }
      case 40: {
        event.preventDefault();
        focusNextItem();
        break;
      }
      default:
        break;
    }
  };

  const focusPrevItem = () => {
    if (focusedBtnIndex.value > 0) {
      focusItemByIndex(focusedBtnIndex.value - 1);
    }
  };

  const focusNextItem = () => {
    if (focusedBtnIndex.value + 1 < dropdownModalButtonRefs.value.length) {
      focusItemByIndex(focusedBtnIndex.value + 1);
    }
  };

  const focusItemByIndex = (buttonIndex: number) => {
    if (!dropdownModalButtonRefs.value.length) {
      return;
    }

    dropdownModalButtonRefs.value.forEach((button: HTMLElement, index: number) => {
      if (index === buttonIndex) {
        button.focus();

        focusedBtnIndex.value = buttonIndex;
      }
    });
  };

  watch(showModal, async (state: boolean) => {
    await nextTick();

    toggleArrowsNavListener(state);
    setDropdownModalPosition();
    toggleDropdownMousedownEventListener(state);
  });

  onMounted(() => {
    dropdownModalButtonRefs.value = [];
  });

  onBeforeUnmount(() => {
    toggleArrowsNavListener(false);
    toggleDropdownMousedownEventListener(false);
  });
</script>
